import { CONSENT_PREVIEW, GTM_ID, MENTAL_CAPACITY_ANALYSIS_PRICE, PURCHASE_PRICE } from '../config';
import { isSensitiveLocation, marketingQuery, safePageLocation } from './analyticsLocation';
import { deleteGoogleCookies, readConsent, setGoogleAnalyticsDisabled, storeConsent } from './analyticsConsent';
import type { ConsentChoice } from './analyticsConsent';

// The banner and the footer read the choice through here, so they need one import.
export { readConsent } from './analyticsConsent';

/**
 * Google Tag Manager for the app. The container (REACT_APP_GTM_ID, set for live only) is the one
 * the marketing site and the LPA service load, so one GA4 property follows a visitor across all
 * three. The container's GA4 tag has automatic page views off and sends page_view on the
 * `virtual_page_view` event pushed here.
 *
 * STRICT CONSENT: the container is loaded only once the customer has accepted analytics cookies
 * (analyticsConsent.ts, a cookie shared with the marketing site). Until then, and after a
 * rejection, no Google script runs and nothing is sent. Withdrawing consent switches every push
 * off at once, tells the loaded container consent is denied and deletes Google's cookies.
 *
 * The container is also never loaded while the address bar shows a URL analytics must not see
 * (analyticsLocation.ts). The rules mirror src/analytics in lpa.ui.
 *
 * EVENTS follow GA4's naming rules (letters, digits and underscores; the old "Main Step Changed"
 * names with spaces are gone): the four step events below, then begin_checkout, add_payment_info
 * and purchase in GA4's ecommerce shape. GTM's data layer is a persistent merged model, where a
 * key pushed once keeps its value for every later event on the page, so every push resets the
 * whole flat parameter set (FLAT_KEYS) and clears `ecommerce` first.
 *
 * NO USER ID IS SENT (decided 24 September 2026). The LPA sends none either, and GA4's User-ID
 * feature would first need a notice in the Data policy. Adding it later means putting `user_id`
 * on every push here and mapping it into the container's GA4 tag.
 */

// The Google Tag Manager data layer. It exists if and only if the container was installed on this
// page (installGtmWhenSafe); nothing else creates it.
declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

interface EcommerceItem {
  item_name: string;
  price: number;
  quantity: number;
}

interface Ecommerce {
  transaction_id?: string;
  value: number;
  currency: string;
  coupon?: string;
  items: EcommerceItem[];
}

export interface PurchaseData {
  transactionId: string;
  value: number;
  currency: string;
  coupon?: string;
  snapshotPrice: number;
  addAnalysis: boolean;
  addLifetimeUpdates: boolean;
}

const CURRENCY = 'GBP';

// Item names match the order recap shown on the checkout page.
const ITEM_SECURE_SETUP = 'Capacity Vault Secure Setup';
const ITEM_ANALYSIS = 'Capacity Snapshot Analysis';
const ITEM_LIFETIME_UPDATES = 'Lifetime Updates';

// GTM only loads where REACT_APP_GTM_ID is set (live). Staging and local leave it empty.
const isConfigured = (): boolean => Boolean(GTM_ID);

// Whether the container script has been added to this page.
const isInstalled = (): boolean => Array.isArray(window.dataLayer);

// The one switch for every push: container loaded AND consent still granted.
export const analyticsEnabled = (): boolean => isInstalled() && readConsent() === 'granted';

// Whether the banner and the footer's "Cookie settings" should exist: wherever a container is
// configured (live), plus staging and local builds (REACT_APP_CONSENT_PREVIEW), which show them
// for testing and load nothing.
export const consentUiEnabled = (): boolean => isConfigured() || CONSENT_PREVIEW;

// Anything that changes the answers above (an install, a consent change) tells these.
const listeners = new Set<() => void>();

export const onAnalyticsChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notifyAnalyticsChange = (): void => listeners.forEach((listener) => listener());

// The footer's "Cookie settings" asks the banner to show itself again.
const REVIEW_EVENT = 'analytics:consent-review';

export const requestConsentReview = (): void => {
  window.dispatchEvent(new Event(REVIEW_EVENT));
};

export const onConsentReviewRequested = (listener: () => void): (() => void) => {
  window.addEventListener(REVIEW_EVENT, listener);
  return () => window.removeEventListener(REVIEW_EVENT, listener);
};

// The campaign parameters the visit arrived with, held until the first page view that is actually
// reported. The affiliate landing and recording-token pages navigate on as soon as they mount,
// which drops the query string, and GA4 attributes the whole session from that first page view.
let landingCampaign = '';

const push = (data: Record<string, unknown>): void => {
  const layer = window.dataLayer;
  if (!layer || !analyticsEnabled()) {
    return;
  }

  layer.push(data);
};

// The gtag() command function from Google's snippet. Commands MUST be pushed as an `arguments`
// object, not a plain array (gtag.js only recognises the former), so this is a classic function.
function gtag(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments as unknown as Record<string, unknown>);
}

// The flat parameters any event may set. Every push carries all of them, with the ones an event
// does not use set explicitly to undefined, so nothing lingers from a previous event.
const FLAT_KEYS = ['step_name', 'step_index', 'video'] as const;

// "Fill the form" → "fill_the_form", "TutorialStep" → "tutorial_step".
const snake = (value: string): string =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[\s-]+/g, '_').toLowerCase();

// Pushes one event, carrying exactly `params` (and `ecommerce`, when given): never a previous
// event's leftovers. A no-op without a running container or consent (push).
const track = (event: string, params: Record<string, unknown> = {}, ecommerce?: Ecommerce): void => {
  const reset = FLAT_KEYS.reduce<Record<string, unknown>>((keys, key) => ({ ...keys, [key]: undefined }), {});
  push({ ecommerce: null });
  push({ event, ...reset, ...params, ...(ecommerce ? { ecommerce } : {}) });
};

const pushEcommerce = (event: string, ecommerce: Ecommerce): void => track(event, {}, ecommerce);

const item = (name: string, price: number): EcommerceItem => ({ item_name: name, price, quantity: 1 });

// The cart before any options are chosen.
const baseCart = (): Ecommerce => ({
  currency: CURRENCY,
  value: parseFloat(PURCHASE_PRICE),
  items: [item(ITEM_SECURE_SETUP, parseFloat(PURCHASE_PRICE))]
});

// Installs the container, but only when the customer has accepted analytics cookies, it is not
// installed already, and the address bar shows nothing analytics must never see. Called at boot,
// on every route change and the moment the customer accepts; a call that finds the moment wrong
// just returns false.
const installGtmWhenSafe = (): boolean => {
  if (!isConfigured() || isInstalled() || typeof document === 'undefined') {
    return false;
  }

  if (readConsent() !== 'granted') {
    return false;
  }

  if (isSensitiveLocation(window.location.pathname, window.location.search)) {
    return false;
  }

  // Consent Mode v2 goes on the data layer BEFORE the container starts: everything denied by
  // default, then analytics storage granted. Advertising storage is never granted.
  window.dataLayer = [];
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied'
  });
  gtag('consent', 'update', { analytics_storage: 'granted' });
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  notifyAnalyticsChange();
  return true;
};

// Called once, before React renders: the only moment the URL the visit ARRIVED with can still be
// read. Remembers its campaign, then loads the container if the customer has already accepted and
// the URL is safe; otherwise the load waits for the acceptance or the first safe route.
export const initAnalytics = (): void => {
  if (!isConfigured() || typeof window === 'undefined') {
    return;
  }

  landingCampaign = marketingQuery(window.location.search);
  installGtmWhenSafe();
};

// One page view per route, named as in the LPA UI: the shared container's GA4 tag has automatic
// page views off and sends page_view on this event instead. The reported URL keeps marketing
// parameters only, and a sensitive URL is never reported (analyticsLocation.ts). Loads the
// container first if it was waiting for consent or a safe URL.
export const trackPageView = (pathname: string): void => {
  installGtmWhenSafe();
  if (!analyticsEnabled()) {
    return;
  }

  const { origin, search } = window.location;
  if (isSensitiveLocation(pathname, search)) {
    return;
  }

  const landing = landingCampaign;
  landingCampaign = '';
  push({
    event: 'virtual_page_view',
    page_path: pathname,
    page_location: safePageLocation(origin, pathname, search, landing)
  });
};

// The customer answered the banner. Accepting loads the container there and then (or switches it
// back on) and reports the page they are on; rejecting, or withdrawing, switches everything off
// and deletes Google's cookies. From the next page load a rejected container is simply not there.
export const recordConsent = (choice: ConsentChoice): void => {
  const wasInstalled = isInstalled();
  storeConsent(choice);

  if (choice === 'granted') {
    if (wasInstalled) {
      setGoogleAnalyticsDisabled(false);
      gtag('consent', 'update', { analytics_storage: 'granted' });
    }
    notifyAnalyticsChange();
    trackPageView(window.location.pathname);
    return;
  }

  if (wasInstalled) {
    gtag('consent', 'update', { analytics_storage: 'denied' });
    setGoogleAnalyticsDisabled(true);
  }
  deleteGoogleCookies();
  notifyAnalyticsChange();
};

// The customer reached a step of the main wizard. `step` is the MainStepEnum name, reported as
// tutorial_step, account_creation_step, recording_step, details_step, payment_step or
// final_steps_step.
export const trackMainStep = (step: string, index: number): void => {
  track('main_step_reached', { step_name: snake(step), step_index: index });
};

export const trackTutorialStep = (index: number): void => {
  track('tutorial_step_reached', { step_index: index });
};

// `video` is the clip the customer is answering, so drop-off can be seen per question.
export const trackRecordingStep = (index: number, video: string | undefined): void => {
  track('recording_step_reached', { step_index: index, video });
};

// device_check, screen_check or fill_the_form.
export const trackAccountCreationStep = (step: string, index: number): void => {
  track('account_creation_step_reached', { step_name: snake(step), step_index: index });
};

// The account now exists: the funnel's equivalent of "user starts an LPA".
export const trackBeginCheckout = (): void => {
  pushEcommerce('begin_checkout', baseCart());
};

export const trackAddPaymentInfo = (): void => {
  pushEcommerce('add_payment_info', baseCart());
};

// Only after Stripe confirms a payment; £0 completions never get here.
export const trackPurchase = (purchase: PurchaseData): void => {
  const items = [item(ITEM_SECURE_SETUP, purchase.snapshotPrice)];

  if (purchase.addAnalysis) {
    items.push(item(ITEM_ANALYSIS, parseFloat(MENTAL_CAPACITY_ANALYSIS_PRICE)));
  }

  if (purchase.addLifetimeUpdates) {
    items.push(item(ITEM_LIFETIME_UPDATES, 0));
  }

  pushEcommerce('purchase', {
    transaction_id: purchase.transactionId,
    value: purchase.value,
    currency: purchase.currency,
    coupon: purchase.coupon,
    items
  });
};
